import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { listConnectors } from '../api/connectors.js';
import { uploadWaMediaEndToEnd, type FinalizeResp } from '../api/waMedia.js';
import { ApiError } from '../api/apiClient.js';
import { useUiStore } from '../state/uiStore.js';

/**
 * #164 — Upload de media WhatsApp.
 *
 * Acepta un archivo, lo sube a S3 (presigned PUT) y luego lo finaliza
 * contra Meta. Devuelve el mediaId que el admin puede copiar al portapapeles
 * y usar en templates / messages outbound.
 *
 * Historial: persistimos los uploads del session en localStorage para que
 * el admin no pierda los IDs si recarga (TTL 30 días lado Meta).
 */

const HISTORY_KEY = 'zen.wa-media.history';
const HISTORY_MAX = 50;

interface HistoryEntry {
  mediaId: string;
  fileName: string;
  contentType: string;
  connectorName: string;
  at: string;
}

function loadHistory(): HistoryEntry[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    return raw ? (JSON.parse(raw) as HistoryEntry[]) : [];
  } catch { return []; }
}

function saveHistory(entries: HistoryEntry[]): void {
  try { localStorage.setItem(HISTORY_KEY, JSON.stringify(entries.slice(0, HISTORY_MAX))); }
  catch { /* quota / private mode — ignorable */ }
}

export function WaMediaUpload(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const pushToast = useUiStore((s) => s.pushToast);
  const [connectorId, setConnectorId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);

  useEffect(() => { setHistory(loadHistory()); }, []);

  const connectorsQuery = useQuery({
    queryKey: ['connectors', id],
    queryFn: () => listConnectors(id),
    enabled: Boolean(id),
  });
  const metaConnectors = useMemo(
    () => (connectorsQuery.data ?? []).filter((c) => c.provider === 'meta_native' && c.phoneNumberId),
    [connectorsQuery.data],
  );

  const uploadMut = useMutation({
    mutationFn: async (): Promise<FinalizeResp> => {
      if (!file) throw new Error('Elegí un archivo');
      if (!connectorId) throw new Error('Elegí un connector Meta');
      return uploadWaMediaEndToEnd(id, connectorId, file);
    },
    onSuccess: (res) => {
      const connectorName = metaConnectors.find((c) => c.id === connectorId)?.name ?? connectorId;
      const entry: HistoryEntry = {
        mediaId: res.mediaId,
        fileName: file?.name ?? '?',
        contentType: res.contentType,
        connectorName,
        at: new Date().toISOString(),
      };
      const next = [entry, ...history];
      setHistory(next);
      saveHistory(next);
      pushToast('success', `Media subida: ${res.mediaId}`);
      setFile(null);
    },
    onError: (e: unknown) => pushToast('error', e instanceof ApiError ? e.message : (e as Error).message),
  });

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Upload media WhatsApp</h2>
          <p className="text-sm text-slate-500">
            Subí imágenes, videos, audio o documentos a Meta. Obtené el
            media_id (válido 30 días) para reutilizarlo en templates.
          </p>
        </div>
        <Link to={`/tenants/${id}`} className="text-sm text-brand-600 hover:underline">
          ← Tenant
        </Link>
      </div>

      <div className="mb-4 rounded border border-slate-200 bg-white p-4 space-y-3">
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-slate-700">Connector Meta</span>
          <select
            value={connectorId}
            onChange={(e) => setConnectorId(e.target.value)}
            className="w-full md:w-96 rounded border border-slate-300 bg-white px-2 py-1 text-sm"
          >
            <option value="">— Elegir un connector meta_native —</option>
            {metaConnectors.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} {c.phoneNumberId ? `(phone ${c.phoneNumberId})` : ''}
              </option>
            ))}
          </select>
          {metaConnectors.length === 0 && (
            <p className="mt-1 text-xs text-amber-700">
              No hay connectors meta_native con phoneNumberId. Configurá uno en Conexiones.
            </p>
          )}
        </label>

        <label className="block text-sm">
          <span className="mb-1 block font-medium text-slate-700">Archivo</span>
          <input
            type="file"
            accept="image/*,video/*,audio/*,application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="w-full text-sm"
          />
          {file && (
            <div className="mt-1 text-xs text-slate-600">
              {file.name} · {(file.size / 1024).toFixed(1)} KB · {file.type || 'sin MIME'}
            </div>
          )}
        </label>

        <div>
          <button
            type="button"
            onClick={() => uploadMut.mutate()}
            disabled={!file || !connectorId || uploadMut.isPending}
            className="rounded bg-brand-500 px-4 py-1.5 text-sm text-white hover:bg-brand-600 disabled:opacity-50"
          >
            {uploadMut.isPending ? 'Subiendo…' : 'Subir a Meta'}
          </button>
          <p className="mt-1 text-xs text-slate-500">
            Flujo: presigned PUT a S3 → backend lee el archivo y lo manda a Meta /media.
          </p>
        </div>
      </div>

      {history.length > 0 && (
        <div className="rounded border border-slate-200 bg-white">
          <div className="border-b border-slate-200 px-4 py-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold">Historial reciente</h3>
            <button
              type="button"
              onClick={() => { setHistory([]); saveHistory([]); }}
              className="text-xs text-red-700 hover:underline"
            >
              Borrar historial
            </button>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">media_id</th>
                <th className="px-3 py-2">Archivo</th>
                <th className="px-3 py-2">Tipo</th>
                <th className="px-3 py-2">Connector</th>
                <th className="px-3 py-2">Subido</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {history.map((h) => (
                <tr key={`${h.mediaId}-${h.at}`}>
                  <td className="px-3 py-2 font-mono text-xs">{h.mediaId}</td>
                  <td className="px-3 py-2 text-xs">{h.fileName}</td>
                  <td className="px-3 py-2 font-mono text-xs">{h.contentType}</td>
                  <td className="px-3 py-2 text-xs">{h.connectorName}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{new Date(h.at).toLocaleString()}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => {
                        void navigator.clipboard.writeText(h.mediaId);
                        pushToast('success', 'media_id copiado');
                      }}
                      className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50"
                    >
                      Copiar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
